/**
 * TEMP-H-017: Theme Selection Component
 * 
 * Per-user theme selector for Settings page.
 * Provides instant theme switching with backend persistence.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme, useThemeSwitcher, getAllThemeMetadata, type ThemeId, type ThemeMetadata } from '@/theme';

export interface ThemeSelectorProps {
  /** Additional CSS class name */
  className?: string;
  /** Show success feedback after theme change */
  showFeedback?: boolean;
}

/**
 * Accessible theme selector component
 */
export function ThemeSelector({ className, showFeedback = true }: ThemeSelectorProps) {
  const { themeId } = useTheme();
  const { switchTheme, isLoading } = useThemeSwitcher();
  const [persistenceStatus, setPersistenceStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const themes = getAllThemeMetadata();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Handle theme selection with immediate apply + backend persistence
   */
  const handleThemeChange = useCallback(async (newThemeId: ThemeId) => {
    if (newThemeId === themeId || isLoading) {
      return; // No change needed or already switching
    }

    // Apply theme immediately for instant feedback (ThemeProvider handles backend persistence)
    if (showFeedback) {
      setPersistenceStatus('saving');
    }

    try {
      await switchTheme(newThemeId);
      if (showFeedback) {
        setPersistenceStatus('success');
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        // Clear success message after 2 seconds
        timeoutRef.current = setTimeout(() => setPersistenceStatus('idle'), 2000);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Failed to set theme (includes backend persistence):', error);
      }
      if (showFeedback) {
        setPersistenceStatus('error');
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        // Clear error message after 3 seconds
        timeoutRef.current = setTimeout(() => setPersistenceStatus('idle'), 3000);
      }
    }
  }, [themeId, switchTheme, isLoading, showFeedback]);

  /**
   * Get display name for theme
   */
  const getThemeDisplayName = (theme: ThemeMetadata) => {
    return theme.name;
  };

  /**
   * Get description for theme
   */
  const getThemeDescription = (theme: ThemeMetadata) => {
    return theme.description;
  };

  return (
    <div className={`theme-selector ${className || ''}`}>
      <fieldset 
        className="theme-selector__fieldset"
        disabled={isLoading}
        style={{
          border: 'none',
          padding: 0,
          margin: 0,
          minWidth: 0,
        }}
      >
        <legend 
          id="theme-selector-legend"
          className="theme-selector__legend"
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--fg-primary)',
            marginBottom: 'var(--space-3)',
            padding: 0,
          }}
        >
          Theme
        </legend>

        <div 
          className="theme-selector__options"
          role="radiogroup"
          aria-labelledby="theme-selector-legend"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          {themes.map((theme) => (
            <label
              key={theme.id}
              className="theme-selector__option"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                backgroundColor: themeId === theme.id ? 'var(--bg-interactive-active)' : 'var(--bg-surface)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all var(--motion-duration-fast) var(--motion-ease-standard)',
                opacity: isLoading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading && themeId !== theme.id) {
                  e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (themeId !== theme.id) {
                  e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
                }
              }}
            >
              <input
                type="radio"
                name="theme"
                value={theme.id}
                checked={themeId === theme.id}
                onChange={() => handleThemeChange(theme.id)}
                disabled={isLoading}
                aria-describedby={`theme-${theme.id}-description`}
                style={{
                  marginTop: '2px', // Align with text baseline
                  accentColor: 'var(--accent)',
                }}
              />
              
              <div className="theme-selector__option-content" style={{ flex: 1 }}>
                <div 
                  className="theme-selector__option-name"
                  style={{
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--fg-primary)',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  {getThemeDisplayName(theme)}
                </div>
                
                <div 
                  id={`theme-${theme.id}-description`}
                  className="theme-selector__option-description"
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--fg-muted)',
                    lineHeight: 'var(--line-height-sm)',
                  }}
                >
                  {getThemeDescription(theme)}
                </div>

                {/* Theme preview colors */}
                {theme.preview && (
                  <div 
                    className="theme-selector__preview"
                    style={{
                      display: 'flex',
                      gap: 'var(--space-1)',
                      marginTop: 'var(--space-2)',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: theme.preview.bg,
                        border: '1px solid var(--border-subtle)',
                      }}
                      aria-hidden="true"
                    />
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: theme.preview.accent,
                        border: '1px solid var(--border-subtle)',
                      }}
                      aria-hidden="true"
                    />
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: theme.preview.text,
                        border: '1px solid var(--border-subtle)',
                      }}
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>

        {/* Persistence feedback */}
        {showFeedback && persistenceStatus !== 'idle' && (
          <div 
            className="theme-selector__feedback"
            role="status"
            aria-live="polite"
            style={{
              marginTop: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              backgroundColor: 
                persistenceStatus === 'success' ? 'var(--success-bg-subtle)' :
                persistenceStatus === 'error' ? 'var(--danger-bg-subtle)' :
                'var(--bg-subtle)',
              color: 
                persistenceStatus === 'success' ? 'var(--success)' :
                persistenceStatus === 'error' ? 'var(--danger)' :
                'var(--fg-muted)',
              border: `1px solid ${
                persistenceStatus === 'success' ? 'var(--success-border-subtle)' :
                persistenceStatus === 'error' ? 'var(--danger-border-subtle)' :
                'var(--border-subtle)'
              }`,
            }}
          >
            {persistenceStatus === 'saving' && '💾 Saving theme preference...'}
            {persistenceStatus === 'success' && '✅ Theme saved successfully'}
            {persistenceStatus === 'error' && '⚠️ Failed to save to server (using local storage)'}
          </div>
        )}
      </fieldset>
    </div>
  );
}

/**
 * Compact theme selector for smaller spaces (e.g., header)
 */
export function CompactThemeSelector({ className }: { className?: string }) {
  const { themeId } = useTheme();
  const { toggleTheme, isLoading } = useThemeSwitcher();

  const handleToggle = useCallback(async () => {
    // Use the convenient toggleTheme from useThemeSwitcher
    try {
      await toggleTheme();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Failed to toggle theme:', error);
      }
    }
  }, [toggleTheme]);

  return (
    <button
      className={`compact-theme-selector ${className || ''}`}
      onClick={handleToggle}
      disabled={isLoading}
      aria-label={`Switch to ${themeId === 'agi' ? 'dark' : 'AGI'} theme`}
      title={`Current theme: ${themeId.toUpperCase()}. Click to switch.`}
      style={{
        padding: 'var(--space-2)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--bg-surface)',
        color: 'var(--fg-primary)',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        transition: 'all var(--motion-duration-fast) var(--motion-ease-standard)',
        opacity: isLoading ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}
      onMouseEnter={(e) => {
        if (!isLoading) {
          e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
      }}
    >
      <span aria-hidden="true">
        {themeId === 'agi' ? '🌟' : '🌙'}
      </span>
      <span>{themeId.toUpperCase()}</span>
    </button>
  );
}